/**
 * Qué paso del alta corresponde, calculado desde el servidor.
 *
 * ## Por qué existe
 *
 * `TenantCreateWizard.tsx` guarda el paso activo en `localStorage`. Para un
 * operador nuestro, que trabaja siempre en el mismo equipo, alcanza. **No
 * alcanza para un dueño que se registra en el celular y vuelve desde la
 * computadora**: encuentra el paso 1 vacío aunque su configuración esté
 * guardada, y va a pensar que perdió el trabajo.
 *
 * La fuente de verdad del progreso es
 * `GET /admin/tenants/:tenantId/onboarding-status`. El navegador es, a lo sumo,
 * caché de lo que todavía no se guardó.
 *
 * ## Tres pasos desde el 2026-09-23 (GKTIBO-120)
 *
 * Identidad → lo que tu bot sabe, probándolo → listo.
 *
 * **Conectar un canal dejó de ser un paso del alta.** Era el paso 2, y hacía
 * que el dueño cableara WhatsApp antes de haber visto a su bot contestar una
 * sola vez: apostar a ciegas. Ahora es un segundo momento, en configuración.
 *
 * Lo que NO cambió es `ready_to_activate`: salir en vivo sigue exigiendo un
 * canal, y eso no es política nuestra —sin canal, nadie puede escribirle—. Por
 * eso `faltaCanal` existe aparte: el alta termina, y el aviso se da una vez.
 *
 * ## Por qué es pura
 *
 * No importa nada. Probar «qué paso corresponde» no puede exigir dos
 * navegadores ni un servidor — y el cálculo equivalente del lado del servidor
 * estuvo roto en producción justamente porque probarlo exigía AWS.
 */

import type { RespuestaDeOnboarding } from '@chatbot/shared-types';

export type IdDePaso = 'identidad' | 'conocimiento' | 'listo';

export interface PasoDelAlta {
  id: IdDePaso;
  completo: boolean;
  /**
   * Qué falta, en términos del negocio y sin jerga (regla D6).
   *
   * Siempre presente cuando el paso está incompleto: un paso sin explicación es
   * un dueño mirando un indicador en rojo sin saber qué hacer (regla C7, L34).
   */
  falta?: string;
}

export interface EstadoDerivado {
  pasoActivo: IdDePaso;
  pasos: PasoDelAlta[];
  /** Copia de `ready_to_activate`. **Nunca** se recalcula: dos cálculos divergen. */
  puedeActivar: boolean;
  /** Los ids de los pasos incompletos, en orden. */
  faltantes: IdDePaso[];
  /**
   * Todavía nadie puede escribirle.
   *
   * No bloquea el alta — la decisión del PO es que se avise **una vez**, en la
   * pantalla de cierre, con las dos salidas a la vista. Después queda un
   * indicador discreto en el panel y nada más insiste.
   */
  faltaCanal: boolean;
}

/** Un canal cuenta sólo si puede RECIBIR: `token-only` es un bot sordo. */
function hayCanalConectado(canales: RespuestaDeOnboarding['channels']): boolean {
  return canales.telegram === 'done' || canales.whatsapp === 'done';
}

/**
 * El paso de conocimiento, que ahora incluye haberlo probado.
 *
 * El flujo transaccional cuenta **sólo si hay integración**: sin ERP no hay
 * ningún flujo que configurar. Ver `calcularSiPuedeActivar` en el backend — la
 * regla es la misma de los dos lados a propósito.
 *
 * Y `probado` es nuevo: el alta no termina hasta que el dueño habló con su bot
 * al menos una vez. Esa pantalla es lo único que muestra cuándo el bot contesta
 * sin haber consultado nada, y salir sin mirarla es cómo un cliente se entera
 * antes que él.
 */
function conConocimiento(estado: RespuestaDeOnboarding): boolean {
  return estado.kb === 'synced' && estado.flow === 'done' && estado.probado;
}

function faltaEnConocimiento(estado: RespuestaDeOnboarding): string | undefined {
  if (conConocimiento(estado)) return undefined;
  if (estado.kb === 'missing') {
    return 'Todavía no cargaste qué sabe tu bot sobre tu negocio.';
  }
  if (estado.kb === 'unsynced') {
    return 'Lo que cargaste todavía se está preparando. En unos minutos queda listo.';
  }
  if (estado.flow !== 'done') {
    return 'Falta elegir qué va a hacer tu bot por vos.';
  }
  // Lo único que queda. Se dice como una invitación, no como un reproche: es
  // la parte del alta que al dueño le sirve a él.
  return 'Probá tu bot al menos una vez: escribile como lo haría un cliente.';
}

/**
 * Por qué todavía no se puede salir en vivo.
 *
 * **El canal va primero, aunque además falte conocimiento.** Ponerlo después
 * escondía el dato más accionable justo cuando más servía: quien mira esta
 * lista —el dueño en el cierre, o un operador nuestro en el wizard— necesita
 * saber que falta conectar algo aunque el bot todavía esté aprendiendo. Son
 * dos cosas independientes y se hacen en cualquier orden.
 */
function faltaEnElCierre(estado: RespuestaDeOnboarding): string | undefined {
  if (estado.ready_to_activate) return undefined;
  if (!hayCanalConectado(estado.channels)) {
    if (estado.channels.telegram === 'token-only') {
      return 'Tu bot de Telegram tiene el token cargado, pero todavía no puede recibir mensajes. Falta terminar de conectarlo.';
    }
    return 'Conectá WhatsApp o Telegram — con uno alcanza para que te escriban.';
  }
  if (!conConocimiento(estado)) {
    return 'Cuando termines lo de arriba vas a poder poner tu bot en vivo.';
  }
  // El servidor dice que no y nosotros no sabemos por qué. Decirlo así es
  // mejor que dejar el botón gris y callado (C7, L34).
  return 'Estamos terminando de dejar todo listo. Probá de nuevo en un momento.';
}

/**
 * Traduce la respuesta del servidor a lo que la pantalla necesita.
 *
 * El `erp` **no aparece como paso**: es informativo, no bloqueante (ADR-023).
 * Hay negocios enteros sin sistema que integrar, y mostrarles «te falta la
 * integración» es decirles que les falta algo que no les corresponde.
 */
export function derivarEstado(estado: RespuestaDeOnboarding): EstadoDerivado {
  const conocimientoListo = conConocimiento(estado);

  const pasos: PasoDelAlta[] = [
    {
      id: 'identidad',
      completo: estado.identity === 'done',
      falta: estado.identity === 'done' ? undefined : 'Falta el nombre de tu negocio.',
    },
    {
      id: 'conocimiento',
      completo: conocimientoListo,
      falta: faltaEnConocimiento(estado),
    },
    {
      id: 'listo',
      // `ready_to_activate` NO incluye haber probado (el servidor lo dice en
      // `onboarding.ts`). Sin el `&&`, el paso 3 salía tildado con el 2 activo.
      completo: estado.ready_to_activate && conocimientoListo,
      falta: faltaEnElCierre(estado),
    },
  ];

  // El último paso NO decide el paso activo: es el destino. Un dueño que
  // terminó de enseñarle a su bot ya está ahí, tenga canal o no.
  const faltantesAntesDelCierre = pasos
    .filter((p) => p.id !== 'listo' && !p.completo)
    .map((p) => p.id);

  return {
    pasoActivo: faltantesAntesDelCierre[0] ?? 'listo',
    pasos,
    // Copia, no cálculo. Si el panel decidiera por su cuenta, tarde o temprano
    // diría que sí cuando el servidor dice que no.
    puedeActivar: estado.ready_to_activate,
    faltantes: pasos.filter((p) => !p.completo).map((p) => p.id),
    faltaCanal: !hayCanalConectado(estado.channels),
  };
}

/** Que no vuelva a preguntar. El valor que `refetchInterval` entiende como «basta». */
export const NO_PREGUNTAR_MAS = false as const;

/** Cada cuánto, mientras haya algo que pueda cambiar solo. */
const CADA_CUANTO_MS = 5_000;

/**
 * ¿Hay que volver a preguntarle al servidor?
 *
 * **Sólo cuando lo que falta se resuelve sin el dueño.** Ese caso es uno solo:
 * `kb: 'unsynced'`, que significa que el conocimiento está guardado y el
 * pipeline todavía lo está indexando. Termina en segundos y nadie le avisa a
 * la pantalla.
 *
 * Sin esto el alta se quedaba clavada: el dueño terminaba la entrevista, veía
 * «tu bot ya sabe lo que necesita» y el botón de seguir no aparecía nunca.
 * Recargar lo arreglaba — pero para saber que hay que recargar hay que
 * sospechar que hay un bug.
 *
 * **Y no se pregunta cuando falta algo que depende del dueño.** Ni un canal sin
 * conectar ni una prueba sin hacer se resuelven solos: ahí el polling sería
 * golpear el endpoint cada cinco segundos, en cada pestaña abierta, sin
 * ninguna posibilidad de novedad.
 */
export function cadaCuantoPreguntar(estado: RespuestaDeOnboarding): number | false {
  if (estado.ready_to_activate) return NO_PREGUNTAR_MAS;
  return estado.kb === 'unsynced' ? CADA_CUANTO_MS : NO_PREGUNTAR_MAS;
}
